import { type FC, type ReactNode } from "react";
import {
  DashboardKpiCard,
  type DefinitionTooltipInfo,
} from "src/components/common";
import type { MetaAdsKpiItem } from "src/services/MetaAdsDashboardAPI";
import {
  META_KPI_FLAT,
  META_KPI_LOWER_IS_BETTER,
  formatMetaKpiTitle,
  type MetaKpiMetricId,
} from "./metaKpiConfig";

const SparklineKpiCard: FC<{
  title: string;
  value: string;
  icon: ReactNode;
  tooltipAlign?: "start" | "end";
  tooltip: DefinitionTooltipInfo;
  comparisonLabel: string;
  noTrendLabel: string;
  noTrendTooltip: string;
  formulaTitle: string;
  changePercent?: number | null;
  changePercentFormattedValue?: string;
  trendPolarity?: "higher-is-better" | "lower-is-better";
}> = ({
  title,
  value,
  icon,
  tooltipAlign = "start",
  tooltip,
  comparisonLabel,
  noTrendLabel,
  noTrendTooltip,
  formulaTitle,
  changePercent,
  changePercentFormattedValue,
  trendPolarity,
}) => {
  const isLongTitle = title.length > 22;

  return (
    <DashboardKpiCard
      className={`paid-media-kpi-card ${
        isLongTitle ? "paid-media-kpi-card--long-title" : ""
      }`}
      title={title}
      value={value}
      icon={icon}
      changePercent={changePercent}
      changePercentFormattedValue={changePercentFormattedValue}
      changePercentTreatZeroAsMissing={false}
      hideNoTrendFallback
      comparisonLabel={comparisonLabel}
      noTrendLabel={noTrendLabel}
      noTrendTooltip={noTrendTooltip}
      formulaTitle={formulaTitle}
      tooltip={tooltip}
      tooltipAlign={tooltipAlign}
      metricLabelForTooltip={title}
      trendPolarity={trendPolarity}
    />
  );
};

interface MetaKpiGridProps {
  liveKpis: MetaAdsKpiItem[];
  kpiIcons: ReactNode[];
  getKpiTooltipForItem: (_kpi: MetaAdsKpiItem) => DefinitionTooltipInfo;
  getKpiTooltipForMetric: (_metricId: MetaKpiMetricId) => DefinitionTooltipInfo;
  getDisplayedKpiTitle: (_metricId: MetaKpiMetricId) => string;
  getDisplayedKpiValue: (_metricId: MetaKpiMetricId) => string;
  comparisonLabel: string;
  noTrendLabel: string;
  noTrendTooltip: string;
  formulaTitle: string;
}

const MetaKpiGrid: FC<MetaKpiGridProps> = ({
  liveKpis,
  kpiIcons,
  getKpiTooltipForItem,
  getKpiTooltipForMetric,
  getDisplayedKpiTitle,
  getDisplayedKpiValue,
  comparisonLabel,
  noTrendLabel,
  noTrendTooltip,
  formulaTitle,
}) => {
  return (
    <div className="paid-media-kpi-grid">
      {liveKpis.length > 0
        ? liveKpis.map((kpi, index) => (
            <SparklineKpiCard
              key={kpi.id}
              title={formatMetaKpiTitle(kpi.title)}
              value={kpi.formatted_value}
              icon={kpiIcons[index]}
              tooltipAlign={index % 5 >= 3 ? "end" : "start"}
              tooltip={getKpiTooltipForItem(kpi)}
              comparisonLabel={comparisonLabel}
              noTrendLabel={noTrendLabel}
              noTrendTooltip={noTrendTooltip}
              formulaTitle={formulaTitle}
              changePercent={kpi.percent_change}
              changePercentFormattedValue={kpi.percent_changeFormattedValue?.trim() || undefined}
              trendPolarity={META_KPI_LOWER_IS_BETTER.has(kpi.id?.trim().toLowerCase() as MetaKpiMetricId) ? "lower-is-better" : "higher-is-better"}
            />
          ))
        : META_KPI_FLAT.map((metricId, index) => (
            <SparklineKpiCard
              key={metricId}
              title={getDisplayedKpiTitle(metricId)}
              value={getDisplayedKpiValue(metricId)}
              icon={kpiIcons[index]}
              tooltipAlign={index % 5 >= 3 ? "end" : "start"}
              tooltip={getKpiTooltipForMetric(metricId)}
              comparisonLabel={comparisonLabel}
              noTrendLabel={noTrendLabel}
              noTrendTooltip={noTrendTooltip}
              formulaTitle={formulaTitle}
              trendPolarity={META_KPI_LOWER_IS_BETTER.has(metricId) ? "lower-is-better" : "higher-is-better"}
            />
          ))}
    </div>
  );
};

export default MetaKpiGrid;
